import React, { useState, useMemo, useCallback } from 'react';
import { EventInstance, EventInstanceSite, EventSiteRelease } from '../../../types';
import { SiteDisplayInfo } from '../../../types/site';
import { groupEventService } from '../../../services/groupEventService';
import { siteService, getStoreSiteTypes, getStoreSiteSections } from '../../../services/siteService';
import { authService } from '../../../services/auth';
import { formatCurrency } from '../../../utils/formatters';
import { formatDate } from '../../../utils/dateUtils';

interface EventSiteReleaseManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (releases: EventSiteRelease[]) => void;
  event: EventInstance;
  assignedSites: EventInstanceSite[];
  siteReleases: EventSiteRelease[];
  loading?: boolean;
}

const formatDateRange = (startDate: string, endDate: string): string => {
  if (!startDate || !endDate) return 'No dates set';
  // Event dates are calendar days (YYYY-MM-DD); formatDate keeps them local.
  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

const EventSiteReleaseManager: React.FC<EventSiteReleaseManagerProps> = ({
  isOpen,
  onClose,
  onSave,
  event,
  assignedSites,
  siteReleases,
  loading: externalLoading = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSection, setSelectedSection] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'released' | 'assigned'>('all');
  const [releases, setReleases] = useState<Map<number, boolean>>(() => {
    const map = new Map<number, boolean>();
    siteReleases.forEach(release => {
      map.set(release.site_id, release.released);
    });
    return map;
  });
  const [saving, setSaving] = useState(false);
  const [siteDetails, setSiteDetails] = useState<Map<number, SiteDisplayInfo>>(new Map());
  const [loadingSiteDetails, setLoadingSiteDetails] = useState(false);

  const storeId = authService.getStoreId();

  // Load site details - optimized to use 3 calls instead of N×4
  const loadSiteDetails = useCallback(async () => {
    if (assignedSites.length === 0) return;

    try {
      setLoadingSiteDetails(true);

      // Fetch lookup data and sites in parallel (3 calls total)
      const [types, sections, sitesData] = await Promise.all([
        getStoreSiteTypes(storeId || undefined),
        getStoreSiteSections(storeId || undefined),
        siteService.getSites(storeId || undefined)
      ]);

      // Create lookup maps for efficient name resolution
      const typeMap = new Map(types.map(t => [t.id, t.name || '']));
      const sectionMap = new Map(sections.map(s => [s.id, s.name]));

      // Transform sites with mapped names and create lookup map
      const siteMap = new Map<number, SiteDisplayInfo>();
      sitesData.forEach(site => {
        siteMap.set(site.id, {
          ...site,
          type_name: site.type_id ? typeMap.get(site.type_id) : undefined,
          section_name: site.site_section_id ? sectionMap.get(site.site_section_id) : undefined,
          status: site.seasonal ? 'seasonal' as const : 'available' as const,
        });
      });

      setSiteDetails(siteMap);
    } catch (err) {
      console.error('Error loading site details:', err);
    } finally {
      setLoadingSiteDetails(false);
    }
  }, [assignedSites.length, storeId]);

  // Reset state and load site details when modal opens
  React.useEffect(() => {
    if (isOpen) {
      const map = new Map<number, boolean>();
      siteReleases.forEach(release => {
        map.set(release.site_id, release.released);
      });
      setReleases(map);
      setSearchTerm('');
      setSelectedSection('all');
      setFilterStatus('all');

      // Load site details
      loadSiteDetails();
    }
  }, [isOpen, siteReleases, loadSiteDetails]);

  // Get full site data for assigned sites
  const sitesWithData = useMemo(() => {
    return assignedSites.map(assigned => {
      const siteData = siteDetails.get(assigned.site_id);
      return {
        ...assigned,
        site: siteData,
        isReleased: releases.get(assigned.site_id) || false
      };
    });
  }, [assignedSites, releases, siteDetails]);

  // Get unique sections
  const sections = useMemo(() => {
    const sectionSet = new Set<string>();
    sitesWithData.forEach(s => {
      if (s.site?.section_name) {
        sectionSet.add(s.site.section_name);
      }
    });
    return Array.from(sectionSet).sort();
  }, [sitesWithData]);

  // Filter sites
  const filteredSites = useMemo(() => {
    return sitesWithData.filter(siteAssignment => {
      const site = siteAssignment.site;
      if (!site) return false;

      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesSearch =
          site.alias?.toLowerCase().includes(search) ||
          site.description?.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }

      // Section filter
      if (selectedSection !== 'all' && site.section_name !== selectedSection) {
        return false;
      }

      // Status filter
      if (filterStatus === 'released' && !siteAssignment.isReleased) {
        return false;
      }
      if (filterStatus === 'assigned' && siteAssignment.isReleased) {
        return false;
      }

      return true;
    });
  }, [sitesWithData, searchTerm, selectedSection, filterStatus]);

  // Group sites by section
  const groupedSites = useMemo(() => {
    const groups: { [key: string]: typeof filteredSites } = {};

    filteredSites.forEach(siteAssignment => {
      const section = siteAssignment.site?.section_name || 'Other';
      if (!groups[section]) {
        groups[section] = [];
      }
      groups[section].push(siteAssignment);
    });

    // Sort sites within each group by alias
    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) =>
        (a.site?.alias || '').localeCompare(b.site?.alias || '', undefined, { numeric: true })
      );
    });

    return groups;
  }, [filteredSites]);

  const toggleRelease = (siteId: number) => {
    setReleases(prev => {
      const next = new Map(prev);
      next.set(siteId, !prev.get(siteId));
      return next;
    });
  };

  const releaseAllVisible = () => {
    setReleases(prev => {
      const next = new Map(prev);
      filteredSites.forEach(site => {
        next.set(site.site_id, true);
      });
      return next;
    });
  };

  const unreleaseAllVisible = () => {
    setReleases(prev => {
      const next = new Map(prev);
      filteredSites.forEach(site => {
        next.set(site.site_id, false);
      });
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Compare current releases with original to find changes
      const originalReleaseMap = new Map<number, boolean>();
      siteReleases.forEach(r => originalReleaseMap.set(r.site_id, r.released));

      // Process each site that has changed
      const releaseEntries = Array.from(releases.entries());
      for (const [siteId, shouldBeReleased] of releaseEntries) {
        const wasReleased = originalReleaseMap.get(siteId) || false;

        if (shouldBeReleased !== wasReleased) {
          if (shouldBeReleased) {
            await groupEventService.releaseSite(event.id, siteId);
          } else {
            await groupEventService.unreleaseSite(event.id, siteId);
          }
        }
      }

      // Create the updated release records to pass back
      const releaseRecords: EventSiteRelease[] = releaseEntries.map(([siteId, released]) => {
        const existing = siteReleases.find(r => r.site_id === siteId);
        return {
          id: existing?.id || 0,
          instance_id: event.id,
          site_id: siteId,
          released,
          released_at: released ? new Date().toISOString() : undefined,
          released_by: released ? authService.getStoreId() || undefined : undefined
        };
      });

      onSave(releaseRecords);
      onClose();
    } catch (err) {
      console.error('Error saving releases:', err);
      alert('Failed to save release settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const isLoading = externalLoading || loadingSiteDetails;
  const releasedCount = sitesWithData.filter(s => s.isReleased).length;
  const assignedCount = sitesWithData.filter(s => !s.isReleased).length;
  const totalCount = sitesWithData.length;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content site-release-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="site-selector-header-content">
            <h2>Manage Site Releases</h2>
            <div className="site-selector-event-info">
              <span className="event-name">{event.name}</span>
              <span className="event-dates">{formatDateRange(event.start_date, event.end_date)}</span>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="release-summary-bar">
          <div className="release-stat">
            <span className="stat-value">{totalCount}</span>
            <span className="stat-label">Total Sites</span>
          </div>
          <div className="release-stat assigned">
            <span className="stat-value">{assignedCount}</span>
            <span className="stat-label">Assigned to Event</span>
          </div>
          <div className="release-stat released">
            <span className="stat-value">{releasedCount}</span>
            <span className="stat-label">Released to Public</span>
          </div>
        </div>

        <div className="site-selector-filters">
          <div className="filter-row">
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Search sites..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <select
              value={selectedSection}
              onChange={(e) => setSelectedSection(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Sections</option>
              {sections.map(section => (
                <option key={section} value={section}>
                  {section}
                </option>
              ))}
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as 'all' | 'released' | 'assigned')}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="assigned">Assigned Only</option>
              <option value="released">Released Only</option>
            </select>
          </div>

          <div className="selection-actions">
            <button className="btn-link" onClick={releaseAllVisible}>
              Release All Visible
            </button>
            <span className="separator">|</span>
            <button className="btn-link" onClick={unreleaseAllVisible}>
              Unrelease All Visible
            </button>
          </div>
        </div>

        <div className="site-selector-body">
          {isLoading ? (
            <div className="loading-message">Loading...</div>
          ) : assignedSites.length === 0 ? (
            <div className="no-sites-message">
              <p>No sites assigned to this event yet.</p>
              <p className="hint">Use the "Sites" button to assign sites first.</p>
            </div>
          ) : Object.keys(groupedSites).length === 0 ? (
            <div className="no-sites-message">
              No sites match your filters
            </div>
          ) : (
            Object.entries(groupedSites).map(([sectionName, sites]) => (
              <div key={sectionName} className="site-section">
                <h3 className="section-header">{sectionName}</h3>
                <div className="site-list">
                  {sites.map(siteAssignment => {
                    const site = siteAssignment.site;
                    if (!site) return null;

                    return (
                      <div
                        key={siteAssignment.site_id}
                        className={`site-item release-item ${siteAssignment.isReleased ? 'released' : 'assigned'}`}
                        onClick={() => toggleRelease(siteAssignment.site_id)}
                      >
                        <div className="site-info">
                          <div className="site-name-row">
                            <span className="site-alias">{site.alias}</span>
                            {site.type_name && (
                              <span className="site-type-badge">{site.type_name}</span>
                            )}
                          </div>
                          {site.description && (
                            <div className="site-description">{site.description}</div>
                          )}
                        </div>
                        <div className="site-price">
                          {siteAssignment.nightly_price
                            ? `${formatCurrency(Number(siteAssignment.nightly_price))}/night`
                            : site.current_price
                              ? `${formatCurrency(site.current_price)}/night`
                              : '-'}
                        </div>
                        <div className="release-toggle">
                          <button
                            className={`toggle-btn ${siteAssignment.isReleased ? 'released' : 'assigned'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRelease(siteAssignment.site_id);
                            }}
                          >
                            {siteAssignment.isReleased ? 'Released' : 'Assigned'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="modal-footer site-selector-footer">
          <div className="selection-summary">
            <strong>{releasedCount}</strong> site{releasedCount !== 1 ? 's' : ''} released to public booking
          </div>
          <div className="footer-actions">
            <button className="btn-cancel" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button className="btn-save" onClick={handleSave} disabled={saving || isLoading}>
              {saving ? 'Saving...' : 'Save Release Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventSiteReleaseManager;
